import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { VideoSectionContent, CommunityPageSectionRow } from '@/types/cms';
import { Save, X, Play, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VideoSectionEditorProps {
  section: CommunityPageSectionRow;
  onSave: (updates: Partial<CommunityPageSectionRow>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const VideoSectionEditor: React.FC<VideoSectionEditorProps> = ({
  section,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const content = section.content as VideoSectionContent;
  
  const [formData, setFormData] = useState({
    title: section.title || '',
    video_url: content.video_url || '',
    video_thumbnail: content.video_thumbnail || '',
    video_description: content.video_description || '',
    video_type: content.video_type || 'youtube',
    autoplay: content.autoplay || false,
    controls: content.controls !== false, // Default to true
    layout: content.layout || 'center'
  });

  useEffect(() => {
    setFormData({
      title: section.title || '',
      video_url: content.video_url || '',
      video_thumbnail: content.video_thumbnail || '',
      video_description: content.video_description || '',
      video_type: content.video_type || 'youtube',
      autoplay: content.autoplay || false,
      controls: content.controls !== false,
      layout: content.layout || 'center'
    });
  }, [section, content]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const { title, ...contentFields } = formData;
    onSave({
      title: title,
      content: {
        ...content,
        ...contentFields
      }
    });
  };

  const getVideoTypeHelp = () => {
    switch (formData.video_type) {
      case 'youtube':
        return 'YouTube URLs: https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID';
      case 'vimeo':
        return 'Vimeo URLs: https://vimeo.com/VIDEO_ID';
      case 'direct':
        return 'Direct video file URLs: https://example.com/video.mp4';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">Edit Video Section</h4>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="video-title">Section Title</Label>
          <Input
            id="video-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter section title"
          />
        </div>

        {/* Video Type */}
        <div className="space-y-2">
          <Label htmlFor="video-type">Video Type</Label>
          <Select
            value={formData.video_type}
            onValueChange={(value) => handleInputChange('video_type', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="youtube">YouTube</SelectItem>
              <SelectItem value="vimeo">Vimeo</SelectItem>
              <SelectItem value="direct">Direct Video File</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Video URL */}
        <div className="space-y-2">
          <Label htmlFor="video-url">Video URL</Label>
          <Input
            id="video-url"
            value={formData.video_url}
            onChange={(e) => handleInputChange('video_url', e.target.value)}
            placeholder="Enter video URL"
          />
          {getVideoTypeHelp() && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {getVideoTypeHelp()}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Video Thumbnail */}
        <div className="space-y-2">
          <Label htmlFor="video-thumbnail">Custom Thumbnail URL (Optional)</Label>
          <Input
            id="video-thumbnail"
            value={formData.video_thumbnail}
            onChange={(e) => handleInputChange('video_thumbnail', e.target.value)}
            placeholder="Enter thumbnail image URL"
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to use default thumbnail from video platform
          </p>
        </div>

        {/* Video Description */}
        <div className="space-y-2">
          <Label htmlFor="video-description">Description (Optional)</Label>
          <Textarea
            id="video-description"
            value={formData.video_description}
            onChange={(e) => handleInputChange('video_description', e.target.value)}
            placeholder="Enter video description"
            rows={3}
          />
        </div>

        {/* Layout Settings */}
        <div className="space-y-4 pt-4 border-t">
          <Label>Layout & Behavior Settings</Label>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="video-layout">Layout</Label>
              <Select
                value={formData.layout}
                onValueChange={(value) => handleInputChange('layout', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                  <SelectItem value="full_width">Full Width</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Switch
                id="video-controls"
                checked={formData.controls}
                onCheckedChange={(checked) => handleInputChange('controls', checked)}
              />
              <Label htmlFor="video-controls">Show video controls</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="video-autoplay"
                checked={formData.autoplay}
                onCheckedChange={(checked) => handleInputChange('autoplay', checked)}
              />
              <Label htmlFor="video-autoplay">Autoplay video</Label>
            </div>
            
            {formData.autoplay && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Note: Most browsers block autoplay videos with sound. Muted autoplay is more reliable.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        {/* Preview */}
        {formData.video_url && (
          <div className="space-y-2 pt-4 border-t">
            <Label>Preview</Label>
            <div className="p-4 bg-white border rounded-lg">
              <div className="aspect-video bg-gray-100 rounded flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Play className="h-12 w-12 mx-auto mb-2" />
                  <p className="text-sm font-medium">Video Preview</p>
                  <p className="text-xs">{formData.video_type} • {formData.layout}</p>
                  {formData.title && (
                    <p className="text-xs mt-2 font-medium">{formData.title}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoSectionEditor;